/**
 * **印章按钮。**
 *
 * 「按钮 = 印章 / 贴纸：直角、粗边、反白字。**不是圆角矩形**」
 * （`visual-design.md` §8）。所以这里没有形状上的选项，只有**用哪几块墨**。
 *
 * ## 四种状态全部来自 `state.ts`
 *
 * 这个文件里**没有** `pointerover` —— 一个都没有。所有状态反馈走
 * `createSurface`，于是它和开关、步进器、滑块上的行为逐字一致。
 *
 * hover = **整块抬起来**（块和字一起，2 设计像素），press = 压回纸面。
 * 位移和墨色都是补间过的，见 `motion.ts`。
 *
 * ## 为什么没有第五种变体
 *
 * 一屏里出现两个"实心章"就等于没有主次，而"主次不分"正是
 * 「统一模板 + 换图标」那种工具感的来源。四个变体够表达
 * 「唯一的主动作 / 其余动作 / 次要入口 / 破坏性动作」这四件事。
 */

import { Container, Graphics, Text } from 'pixi.js';
import { GAP, TRACKING, INK, uiPad } from './style';
import { HOLD_CONFIRM_MS } from './hold';
import {
  createSurface,
  drawHoldBar,
  drawRedactionSlash,
  drawStamp,
  type ControlMotion,
  type ControlState,
} from './state';
import { stampInkAt, type StampInk } from './ink';
import { richLabel } from './richText';
import type { Widget } from './base';
import type { StampVariant } from './style';

export interface StampButtonSpec {
  readonly label: string;
  readonly scale: number;
  readonly variant?: StampVariant;
  readonly onPress: () => void;
  /** 不给就按字宽算 */
  readonly width?: number;
  readonly height?: number;
  readonly size?: number;
  readonly enabled?: boolean;
  /** 内边距（设计像素）。不给用默认（x=16 / y=11）。房间页那两个小按钮要更紧凑 */
  readonly padding?: { readonly x?: number; readonly y?: number };
  /** 悬停时给一句说明。`null` = 不显示 */
  readonly hint?: string | null;
  /**
   * **长按确认要按多久**（毫秒）。不给就用 `HOLD_CONFIRM_MS`。
   *
   * ⚠️ 它只是**时长**：按钮一开始永远是普通按钮（松手触发），
   *    要 `setConfirm(true)` 才换成"按住才生效"。见那个方法。
   */
  readonly holdMs?: number;
  /**
   * **触控上按住多久算「看这个按钮的说明」。** 给毫秒数就启用。
   *
   * 触控没有 hover，动作按钮的 tip 只能按住看；按住看过的那一下不再触发 `onPress`。
   * ⚠️ 和 `setConfirm(true)`（按住 = 确认）互斥 —— 确认态下按住是动作本身。
   */
  readonly hintHoldMs?: number;
}

export interface StampButton extends Widget {
  setEnabled(enabled: boolean): void;
  setLabel(text: string): void;
  /**
   * **变成长按确认的样子**：墨换成 `danger`（酸玫红实心），而且按一下不算数，
   * 得按住那条条子涨满。
   *
   * 评审：「在濒死期间，我希望按钮变红，且需要长按确认。」
   *
   * ⚠️ 变红和长按是**同一个开关**：分开两个 setter 迟早会出现"红了但一按就中"
   * 或者"要长按但还是绿的"这种半吊子状态。`false` 会整体退回原样。
   */
  setConfirm(confirm: boolean): void;
  /**
   * **给按钮换一管自定墨水**（不给就是构造时的变体）。
   *
   * 用它把「被攻击的额外回合」按钮染成**来源牌的颜色**（用户要求）——
   * 颜色由调用方从卡牌定义里读出来，这里不认识任何具体牌。
   * 濒死确认态（`setConfirm(true)`）优先级更高，会盖过它。
   */
  setAccent(color: number | null): void;
  /**
   * **换一个印章变体**（实心 / 描边 / 次要 …）。
   *
   * 给「同一颗按钮在开关状态之间切换、又不值得整屏重建」的地方用
   * （设置页的卡牌说明板块就是它）。
   */
  setVariant(variant: StampVariant): void;
  /** 现在自定的墨水色（没设就是 null，读数/冒烟用） */
  readonly accent: number | null;
  /** 现在是不是长按确认的样子（读数/冒烟用） */
  readonly confirm: boolean;
  /** 触控按住够久、正在看这个按钮的说明吗（长按代替 hover） */
  readonly hinting: boolean;
  /** 长按进度 0..1。没在长按的时候是 0（读数/冒烟用 —— 条子画的就是它） */
  readonly hold: number;
  /** 现在印的是什么字（读数/冒烟用） */
  readonly label: string;
  readonly enabled: boolean;
  /** 指针现在停在按钮上吗（hover tooltip 靠它决定显示） */
  readonly hovered: boolean;
}

export function stampButton(spec: StampButtonSpec): StampButton {
  const scale = spec.scale;
  let variant = spec.variant ?? 'secondary';
  const size = spec.size ?? 13;
  const padX = uiPad(spec.padding?.x ?? 16, scale);
  const padY = uiPad(spec.padding?.y ?? 11, scale);

  const container = new Container();
  const background = new Graphics();
  const text: Text = richLabel({
    text: spec.label,
    scale,
    size,
    fill: INK.text,
    role: 'mono',
    bold: true,
    tracking: TRACKING.normal,
  });
  container.addChild(background, text);

  const width = spec.width ?? Math.ceil(text.width + padX * 2);
  const height = spec.height ?? Math.ceil(text.height + padY * 2);

  /**
   * 濒死那种"要长按确认"的样子。见 `setConfirm`。
   *
   * ⚠️ 它是一个**开关**（不是一个变体）：开着的时候墨和触发方式**一起**变，
   * 关掉就整体退回构造时那一套。
   */
  let confirming = false;
  /** 自定墨水色（null = 用构造时的变体）。见 `setAccent` */
  let accent: number | null = null;

  const surface = createSurface({
    container,
    width,
    height,
    ...(spec.enabled === undefined ? {} : { enabled: spec.enabled }),
    onPress: spec.onPress,
    ...(spec.hintHoldMs === undefined ? {} : { hintHoldMs: spec.hintHoldMs }),
    /**
     * ⚠️ 长按的**时长**不从构造里进去：构造时这个按钮还是普通按钮
     *    （"结束回合"松手就该走）。`setConfirm(true)` 才把触发方式换掉。
     *    但 `onConfirm` 现在就给 —— 换过去之后它要能立刻工作。
     */
    onConfirm: spec.onPress,
    onStateChange: (state, motion) => paint(state, motion),
    onFrame: (state, motion) => paint(state, motion),
    focusable: true,
  });

  function paint(state: ControlState, motion: ControlMotion): void {
    // 墨水优先级：濒死确认态（danger）> 自定色（攻击来源牌）> 构造时的变体。
    // 前者是"这一下按出去就出局"的语义，任何装饰都不许盖过它。
    const useAccent = !confirming && accent !== null && state !== 'disabled';
    const ink: StampInk =
      useAccent && accent !== null
        ? { fill: accent, edge: accent, label: INK.onKnockout }
        : stampInkAt(confirming ? 'danger' : variant, state, motion.mix);
    background.clear();
    drawStamp(background, ink, { width, height }, scale, motion.lift);
    if (state === 'disabled') {
      drawRedactionSlash(background, { width, height }, scale, INK.rule, motion.lift);
    }
    // ⚠️ 条子**在字底下**（`background` 是先加进容器的那个孩子），
    //    所以扫过去不会把标签吃掉
    drawHoldBar(background, { width, height }, scale, motion.hold, INK.holdBar, motion.lift);
    text.style.fill = ink.label;
    /**
     * 字跟着块一起抬 —— 是「整块东西被拿起来」，不是「底下的纸鼓起来」。
     *
     * ⚠️ 居中坐标**每次现算**，不要缓存成构造时的常量：`setLabel` 换的是更长
     *    的标签（「结束额外回合」），缓存的话下一次重画会把它摆回旧标签的居中位。
     */
    text.position.set((width - text.width) / 2, (height - text.height) / 2 + motion.lift);
  }

  // ⚠️ **首帧必须自己画一次。** `createSurface` 只在状态**变化**时回调，
  // 而构造出来的那一刻状态已经是 `idle`（不是"从别处变成 idle"），
  // 所以没有任何回调会来。漏掉这一行的表现是：**按钮在闲置时是一块空白**，
  // 鼠标划过去才突然出现 —— 静态断言全绿，只有截图看得见
  paint(surface.state, surface.motion);

  return {
    container,
    width,
    height,
    get enabled() {
      return surface.enabled;
    },
    setEnabled(next: boolean) {
      surface.setEnabled(next);
    },
    setLabel(next: string) {
      if (text.text === next) return;
      text.text = next;
      // 立刻重画一次：居中坐标在 `paint` 里现算，这一笔就把它摆正
      paint(surface.state, surface.motion);
    },
    get confirm() {
      return confirming;
    },
    get hinting() {
      return surface.hinting;
    },
    get hold() {
      return surface.motion.hold;
    },
    get label() {
      return text.text;
    },
    /** 指针现在停在按钮上吗（tooltip 靠它决定显示） */
    get hovered() {
      return surface.state === 'hover';
    },
    setAccent(next: number | null) {
      if (accent === next) return;
      accent = next;
      // 和 setConfirm 同一个理由：换色不是「状态变了」，必须立刻补一笔
      paint(surface.state, surface.motion);
    },
    setVariant(next: StampVariant) {
      if (variant === next) return;
      variant = next;
      // 同 setAccent：换墨不是状态变化，不补这一笔要等到下次 hover 才显形
      paint(surface.state, surface.motion);
    },
    get accent() {
      return accent;
    },
    setConfirm(next: boolean) {
      if (confirming === next) return;
      confirming = next;
      // ⚠️ 触发方式一起换：确认态要长按，平常态松手就走
      surface.setHoldMs(next ? (spec.holdMs ?? HOLD_CONFIRM_MS) : null);
      // ⚠️ 换了样子必须**立刻重画**：`createSurface` 只在状态变的时候回调，
      //    而"变红"这件事不是状态变（还是 idle），不补这一笔就要等到
      //    下一次 hover 才显形（按钮在闲置时是纯黑那一版就是这么来的）
      paint(surface.state, surface.motion);
    },
  };
}

/** 两个控件并排时要留的缝。`GAP.normal` 是唯一的并列间距 */
export const STAMP_GAP = GAP.normal;
